import {
  DHashSimilarity,
  type IReportPhotoDuplicate,
  type IReportPhotoDuplicatePhoto,
} from '@mahawi/eshop-common/src/types';
import { Image, Tag, Typography } from 'antd';
import {
  type ColumnGroupType,
  type ColumnsType,
  type ColumnType,
} from 'antd/es/table';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import Table from '../components/table';
import { getColumnSearch } from '../fragments/table-get-column-search';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { reportPhotoDuplicatesLoad } from '../reducers/report/reducer';
interface DataType extends IReportPhotoDuplicatePhoto {
  key: string;
  dhash: string;
  similarity: DHashSimilarity;
}

function similarityTag(similarity: DHashSimilarity): React.ReactElement {
  if (similarity === DHashSimilarity.IDENTICAL) {
    return <Tag color="red">Identical</Tag>;
  }
  if (similarity === DHashSimilarity.DUPLICATE) {
    return <Tag color="orange">Duplicate</Tag>;
  }
  return <Tag color="gold">Similar</Tag>;
}

function ReportPhotoDuplicatesContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const reportPhotoDuplicates = useSelector(
    (s: RootState) => s.Report.photoDuplicates,
  );
  const reportUpdatedAt = useSelector((s: RootState) => s.Report.updatedAt);
  const reportInProcess = useSelector((s: RootState) => s.Report.inProcess);

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [columns, setColumns] = useState<ColumnsType<DataType>>([]);

  useEffect((): void => {
    const dataSourceUE: DataType[] =
      reportPhotoDuplicates?.flatMap(
        (group: IReportPhotoDuplicate): DataType[] =>
          group.photos.map((photo: IReportPhotoDuplicatePhoto): DataType => ({
            key: `${group.dhash}-${photo.uuid}`,
            dhash: group.dhash,
            similarity: group.similarity,
            ...photo,
          })),
      ) ?? [];

    const columnsUE: ColumnsType<DataType> = [
      {
        title: 'Thumbnail',
        dataIndex: 'uuid',
        render: (uuid: string): React.ReactElement => (
          <Image
            src={`/static/product/photo/${uuid}.webp`}
            width={60}
            style={{ borderRadius: 4, objectFit: 'cover' }}
          />
        ),
        width: '8%',
      },
      {
        title: 'Product',
        dataIndex: 'productName',
        sorter: (a: DataType, b: DataType): number =>
          a.productName.localeCompare(b.productName),
        ...getColumnSearch<DataType>('productName'),
        render: (_: string, record: DataType): React.ReactElement => (
          <Link
            to={`/product/${record.productUuid}`}
            label={record.productName}
            openInNewTab
            additionalSearchParams={{ photo: 'open' }}
          />
        ),
        width: '35%',
      },
      {
        title: 'Position',
        dataIndex: 'position',
        sorter: (a: DataType, b: DataType): number => a.position - b.position,
        align: 'right',
        width: '8%',
      },
      {
        title: 'dhash',
        dataIndex: 'dhash',
        ...getColumnSearch<DataType>('dhash'),
        render: (dhash: string): React.ReactElement => (
          <Typography.Text code>{dhash.slice(0, 8)}…</Typography.Text>
        ),
        width: '15%',
      },
      {
        title: 'Similarity',
        dataIndex: 'similarity',
        filters: [
          { text: 'Identical', value: DHashSimilarity.IDENTICAL },
          { text: 'Duplicate', value: DHashSimilarity.DUPLICATE },
          { text: 'Similar', value: DHashSimilarity.SIMILAR },
        ],
        onFilter: (value, record: DataType): boolean =>
          record.similarity === value,
        render: (s: DHashSimilarity): React.ReactElement => similarityTag(s),
        width: '10%',
      },
    ];

    setDataSource(dataSourceUE);
    setColumns(columnsUE);
  }, [reportPhotoDuplicates, reportUpdatedAt]);

  return (
    <>
      <DetailPageHeader
        title="Photo duplicates"
        onReload={(): void => {
          dispatch(reportPhotoDuplicatesLoad());
        }}
        isReloadDisabled={reportInProcess}
        isReloadHighlighted={!reportPhotoDuplicates}
      />

      <Table
        columns={columns as (ColumnGroupType<object> | ColumnType<object>)[]}
        dataSource={dataSource}
        isLoading={reportInProcess}
        emptyText="No photo duplicates found"
      />
    </>
  );
}

export default ReportPhotoDuplicatesContainer;
